/**
 * Plain ES5 source, inlined into the Datadog RUM and Logs init scripts in `app/layout.tsx`, that removes DocuSign
 * signing URLs (bearer credentials for a patient's signing session) from telemetry before it is sent:
 * - URL fields (`resource.url`, `error.resource.url`, `http.url`) on a DocuSign host are reduced to their origin, so
 *   load timing and failures stay visible.
 * - DocuSign URLs inside messages and stacks are replaced with a fixed placeholder.
 *
 * It declares `nileScrubDocusignRumEvent(event)` and `nileScrubDocusignLogEvent(log)`, which mutate in place and
 * never throw. The inline scripts cannot import modules, so this string is the single shared source.
 */
export const DOCUSIGN_TELEMETRY_SCRUB_SCRIPT = String.raw`
function nileDocusignOrigin(url) {
    if (typeof url !== 'string') return null;
    var match = /^(https?:\/\/)(?:[^\/?#\s@]*@)?([^\/?#\s:]+)(:\d+)?/i.exec(url);
    if (!match || !/(^|\.)docusign\.(net|com)$/i.test(match[2])) return null;
    return match[1] + match[2] + (match[3] || '');
}
function nileScrubDocusignUrlField(holder, key) {
    if (!holder) return;
    var origin = nileDocusignOrigin(holder[key]);
    if (origin) holder[key] = origin;
}
function nileScrubDocusignText(holder, key) {
    if (!holder || typeof holder[key] !== 'string') return;
    holder[key] = holder[key].replace(/https?:\/\/[^\s"'<>)]+/gi, function (url) {
        return nileDocusignOrigin(url) ? '[redacted DocuSign URL]' : url;
    });
}
function nileScrubDocusignRumEvent(event) {
    try {
        if (!event) return;
        nileScrubDocusignUrlField(event.resource, 'url');
        if (event.error) {
            nileScrubDocusignText(event.error, 'message');
            nileScrubDocusignText(event.error, 'stack');
            nileScrubDocusignUrlField(event.error.resource, 'url');
        }
    } catch (error) {}
}
function nileScrubDocusignLogEvent(log) {
    try {
        if (!log) return;
        nileScrubDocusignText(log, 'message');
        if (log.error) {
            nileScrubDocusignText(log.error, 'message');
            nileScrubDocusignText(log.error, 'stack');
        }
        nileScrubDocusignUrlField(log.http, 'url');
    } catch (error) {}
}
`

const DOCUSIGN_HOST_PATTERN = /(^|\.)docusign\.(net|com)$/i
const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>)]+/gi
const URL_HOST_PATTERN = /^https?:\/\/(?:[^\/?#\s@]*@)?([^\/?#\s:]+)/i

/**
 * Replaces every DocuSign URL in `text` with the same placeholder the inline script uses, for telemetry sent outside
 * Datadog (such as Segment client errors). Tests keep it identical to the inline script's text rule.
 */
export function redactDocusignUrls(text: string): string {
    return text.replace(URL_IN_TEXT_PATTERN, (url) => {
        const host = URL_HOST_PATTERN.exec(url)?.[1]
        return host && DOCUSIGN_HOST_PATTERN.test(host) ? '[redacted DocuSign URL]' : url
    })
}

type TelemetryEvent = Record<string, any>

/** Evaluates the inline script so tests exercise exactly what the browser runs. */
export function createDocusignTelemetryScrubbers(): {
    scrubRumEvent: (event: TelemetryEvent) => void
    scrubLogEvent: (log: TelemetryEvent) => void
} {
    return new Function(
        `${DOCUSIGN_TELEMETRY_SCRUB_SCRIPT}; return { scrubRumEvent: nileScrubDocusignRumEvent, scrubLogEvent: nileScrubDocusignLogEvent };`,
    )()
}
